/**
 * Who the header greets: "Good evening, Mr. Singh".
 *
 * The name used to come from the Microsoft sign-in token (the account's display name),
 * saved on the device at login, with "Mr." and its second word. So a name corrected on the
 * school record (Student profile › Family & home) never reached the greeting, every parent
 * was "Mr.", and on a family login - the child's own account - it greeted the child's
 * surname (QA: "Mr. Bush" after the father was renamed Singh).
 *
 * Now it reads the child's relatives as the school holds them (reloaded on every open and
 * after any family edit in the app), and picks the signed-in parent among them.
 */

interface UserLike {
  userId?: string;
  id?: string;
  email?: string;
  name?: string;
}

interface RelativeLike {
  UserId?: string | null;
  userId?: string | null;
  Email?: string | null;
  email?: string | null;
  FirstName?: string | null;
  firstName?: string | null;
  LastName?: string | null;
  lastName?: string | null;
  RelationName?: string | null;
  relationName?: string | null;
  IsActive?: boolean;
  isActive?: boolean;
}

const field = (r: RelativeLike, pascal: keyof RelativeLike, camel: keyof RelativeLike) =>
  String((r[pascal] ?? r[camel] ?? '') as string).trim();

const sameId = (a?: string | null, b?: string | null) =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase();

/** "Mr. Singh" for a father, "Mrs. Harris" for a mother, the full name for anyone else. */
export function greetingNameFor(relative: RelativeLike): string {
  const first = field(relative, 'FirstName', 'firstName');
  const last = field(relative, 'LastName', 'lastName');
  const relation = field(relative, 'RelationName', 'relationName').toLowerCase();
  if (last && relation === 'father') return `Mr. ${last}`;
  if (last && relation === 'mother') return `Mrs. ${last}`;
  return [first, last].filter(Boolean).join(' ');
}

/**
 * The name to greet, or '' to greet without one.
 *  1. The relative whose e-mail is the one signed in - a parent on their own account.
 *  2. The relative(s) linked to this login: one → them; several (a family login both parents
 *     share) → their first names, "Daniel & Patricia".
 *  3. No relative matches → the sign-in account's own name, with no title guessed.
 */
export function parentGreetingName(user: UserLike | null | undefined, relatives: RelativeLike[] | null | undefined): string {
  if (!user) return '';
  const active = (relatives ?? []).filter(r => (r.IsActive ?? r.isActive) !== false);

  const byEmail = active.find(r => sameId(field(r, 'Email', 'email'), user.email));
  if (byEmail) return greetingNameFor(byEmail);

  const loginId = user.userId || user.id;
  const linked = active.filter(r => sameId(field(r, 'UserId', 'userId'), loginId));
  if (linked.length === 1) return greetingNameFor(linked[0]);
  if (linked.length > 1) {
    const firstNames = [...new Set(linked.map(r => field(r, 'FirstName', 'firstName')).filter(Boolean))];
    return firstNames.join(' & ');
  }

  return (user.name ?? '').trim();
}

/** The relatives on a child's portal record (StudentFullSnapshotDto.Relatives). */
export const relativesOf = (student: { rawSnapshot?: any } | null | undefined): RelativeLike[] =>
  student?.rawSnapshot?.Relatives ?? student?.rawSnapshot?.relatives ?? [];
